import {
    ReactElement,
    useEffect,
} from 'react';

import {
    Alert,
    Button,
    Group,
    Stack,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {useDomainNames} from '../hooks/useDomainNames';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {useDomainNamesSelection} from '../hooks/useDomainNamesSelection';
import {DomainName,} from '../types';
import {useParams} from "react-router-dom";
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from "@flexinet/auth-components";
import {DataTableColumn} from 'mantine-datatable';
import {rbacActions} from "../../../config/rbac/actions.ts";
import {useMediaQuery} from '@mantine/hooks';

// FNP-1282: `settings` is gone with SimpleTableBatchAction, which was the only
// thing that rendered it. No caller ever passed it — both IndexPage and ListPage
// render <RecordsTable/> bare — so nothing is lost. `extraTools` is kept because
// it is part of the header contract and still renders.
export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
}) => {

    const {allowedActions} = useVerifiedPermissions()

    const {id: registrarId} = useParams();

    const {
        user
    } = useAuth();


    if (!user) {
        throw new Error('user is required parameter')
    }


    const {
        items,
        visibleItems: records,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading,
        isError,
        isFetchingNextPage,
        isRefetching,
        nextToken,
        fetchRecords,
        reload,
        handleFilterByKeyword,
    } = useDomainNames(registrarId);


    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = useDomainNamesSelection();

    useEffect(() => {
        fetchRecords();
        // FNP-960: deliberately mount-only. The callback is recreated on
        // every render, so adding it to the deps would re-fire in a loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const smallScreen = useMediaQuery('(max-width: 600px)');

    let filteredColumns = columns;
    if (smallScreen) {
        filteredColumns = columns.filter(item => !['created', 'registrarId', 'category', 'tldId'].includes(item.accessor))
    }

    let selectedRecords = [] as DomainName[];
    if (allowedActions.includes(rbacActions.DeleteDomainName)) {
        selectedRecords = Object.keys(selection)
                                .map(key => selection[key])
    }


    // ⚠️ THE EMPTY STATES MEAN DIFFERENT THINGS and `noRecordsText` is one
    // string, so the distinction is branched here rather than flattened into a
    // single message that sends an operator hunting for a bug that is not
    // there. A SEARCHED empty result is not "you have no domain names", and
    // neither is a failed load.
    const searching = keyword.trim() !== '';
    let noRecordsText = I18n.get('No domain names yet');
    if (isError) {
        noRecordsText = I18n.get('Could not load domain names');
    } else if (searching) {
        noRecordsText = I18n.get('No loaded domain name matches this search');
    }

    return (
        <Stack gap='xl' m='sm'>
            {/* TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
                `limit` + `handleItemsPerPageChange` and renders a rows-per-page
                dropdown, which token pagination cannot honour — there is no page
                to resize and no total to divide (§9). */}
            <Group justify={'space-between'} align={'center'}>
                <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>
                {extraTools}
            </Group>

            {/* ⚠️ DISTINCT FROM AN EMPTY TABLE: this is the QUERY failing, so
                nothing at all is known — not "this tenant has no domain names". */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load domain names')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your domain names. The request itself failed, so this list is not empty — it is unknown.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isRefetching}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable
                columns={filteredColumns as DataTableColumn[]}
                records={records}
                // FNP-1282 §3a — part TWO of the sorting contract. ⚠️ THIS IS
                // THE HALF THAT GETS FORGOTTEN: without these two props the
                // `sortable: true` headers render as clickable and do nothing
                // when clicked, which looks functional and is not.
                // `useDomainNames` holds the state and applies the shared
                // comparator; the table only reports the click.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                // ⚠️ THE LOADING STATE, distinct from empty and from error.
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach(item => {
                        handleRowSelection(item as unknown as DomainName)
                    })
                }}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered one.
                filterTerm={searching ? keyword : undefined}
                noun={'domain name'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />

        </Stack>
    );
};
